import React, { Component } from 'react';
import { KeyRound, Lock, Eye, EyeOff } from "lucide-react";
import { logger } from "../../utill/logger";
import { toUserError } from "../../utill/toUserError";
import { Modal, Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import InlineAlert from '../../shared/notificationAlert/inlineAlert';
import { changePasswordApi } from '../../utill/api/loginApi';
import { encryptPassword } from '../../utill/rsaEncrypt';
import '../../assets/styles/_modal.scss';

class ChangePasswordModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      validated: false,
      disable: false,
      noMatch: null,
      show: { oldPassword: false, newPassword: false, confirmPassword: false }
    }
  }

  togglePasswordVisibility = (field) => {
    this.setState((s) => ({ show: { ...s.show, [field]: !s.show[field] } }));
  }

  // Trailing eye toggle for a credential field, styled like the leading lock icon.
  renderEyeToggle = (field) => {
    const visible = this.state.show[field];
    return (
      <InputGroup.Text
        as="button"
        type="button"
        className="form-icon"
        style={{ cursor: "pointer" }}
        onClick={() => this.togglePasswordVisibility(field)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
      >
        {visible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
      </InputGroup.Text>
    );
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const {
      oldPassword,
      newPassword,
      confirmPassword
    } = event.target;
    if (newPassword.value !== confirmPassword.value) {
      this.setState({ noMatch: true });
    } else if (form.checkValidity()) {
      this.setState({ disable: true, noMatch: false });
      try {
        const encryptedOldPassword = await encryptPassword(oldPassword.value);
        const encryptedNewPassword = await encryptPassword(newPassword.value);
        const encryptedConfirmPassword = await encryptPassword(confirmPassword.value);
        const payload = {
          oldPassword: encryptedOldPassword,
          newPassword: encryptedNewPassword,
          confirmPassword: encryptedConfirmPassword
        };
        this.changePassword(payload);
      } catch (encryptionError) {
        this.setState({ error: { message: "Failed to encrypt password" }, disable: false });
      }
    }
    this.setState({ validated: true });
  }

  changePassword = (payload) => {
    changePasswordApi(payload)
      .then(res => {
        if (res.status === 200) {
          res.successCode = "changePasswordSuccess";
          // Forced mode (must-change flow): hand control back to the
          // page-level wrapper so it can re-fetch /loggedin/info and navigate
          // to the dashboard. Don't toast — the wrapper does the success UX.
          if (this.props.forced && this.props.onForceComplete) {
            this.props.onForceComplete();
            this.setState({ disable: false, validated: false });
            return;
          }
          this.props.onHide();
          this.props.showModalSuccess(res);
          this.setState({ disable: false, isConfirmed: false, validated: false });
        } else if (res.status !== 200) {
          res.json().then(res => {
            this.setState({ error: toUserError(res), disable: false });
          })
        }
      })
      .catch(e => {
        this.setState({ error: toUserError(e), disable: false });
        logger.log(e);
      });
  }

  render() {
    const {
      validated,
      disable,
      noMatch,
      show
    } = this.state;
    const formId = "change-password-form";
    const forced = Boolean(this.props.forced);
    return (
      <Modal
        show={this.props.show}
        onHide={forced ? () => {} : this.props.onHide}
        backdrop={'static'}
        keyboard={false}
        aria-labelledby="change-password-title"
        centered
      >
        <Modal.Header closeButton={!forced}>
          <Modal.Title id="change-password-title">
            <KeyRound className="text-primary me-2" size={16} aria-hidden="true" />
            Change Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="schedule-modal-body">
          {forced ? (
            <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
              <Lock size={18} aria-hidden="true" className="mt-1" />
              <div>
                <strong>Password change required.</strong>
                <div className="small">
                  Your administrator has reset your password (or this is your first
                  sign-in). Please pick a new password to continue using the app.
                </div>
              </div>
            </div>
          ) : null}
          {Boolean(this.state.error) ? (
            <InlineAlert error={this.state.error} />
          ) : null}
          <Form
            id={formId}
            noValidate
            encType="multipart/form-data"
            validated={validated}
            onSubmit={this.handleSubmit}
          >
            <Form.Group className="mb-3" controlId="oldPassword">
              <Form.Label>Current password</Form.Label>
              <InputGroup hasValidation>
                <InputGroup.Text className="form-icon">
                  <Lock size={16} aria-hidden="true" />
                </InputGroup.Text>
                <Form.Control
                  required
                  name="oldPassword"
                  type={show.oldPassword ? "text" : "password"}
                  autoComplete="current-password"
                  spellCheck={false}
                  placeholder="Your current password"
                  maxLength={45}
                />
                {this.renderEyeToggle("oldPassword")}
                <Form.Control.Feedback type="invalid">
                  Please enter your current password.
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3" controlId="newPassword">
              <Form.Label>New password</Form.Label>
              <InputGroup hasValidation>
                <InputGroup.Text className="form-icon">
                  <Lock size={16} aria-hidden="true" />
                </InputGroup.Text>
                <Form.Control
                  required
                  name="newPassword"
                  type={show.newPassword ? "text" : "password"}
                  autoComplete="new-password"
                  spellCheck={false}
                  placeholder="Choose a new password"
                  minLength={6}
                  maxLength={45}
                />
                {this.renderEyeToggle("newPassword")}
                <Form.Control.Feedback type="invalid">
                  Please enter a new password (minimum 6 characters).
                </Form.Control.Feedback>
              </InputGroup>
              <Form.Text className="text-muted">
                At least 6 characters. Avoid reusing the current password.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-2" controlId="confirmPassword">
              <Form.Label>Confirm new password</Form.Label>
              <InputGroup hasValidation>
                <InputGroup.Text className="form-icon">
                  <Lock size={16} aria-hidden="true" />
                </InputGroup.Text>
                <Form.Control
                  required
                  name="confirmPassword"
                  type={show.confirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  spellCheck={false}
                  placeholder="Re-enter the new password"
                  maxLength={45}
                  isInvalid={noMatch === true}
                />
                {this.renderEyeToggle("confirmPassword")}
                <Form.Control.Feedback type="invalid">
                  {noMatch ? "Passwords do not match." : "Please re-enter the new password."}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="schedule-modal-footer">
          {/* Cancel hidden in forced mode — the user can't escape the change flow
              by closing the dialog. They can still sign out via the header logout. */}
          {forced ? null : (
            <Button variant="outline-secondary" onClick={this.props.onHide} disabled={disable}>
              Cancel
            </Button>
          )}
          <Button type="submit" form={formId} variant="primary" disabled={disable}>
            {disable ? (
              <Spinner animation="border" variant="light" size="sm" role="status" className="me-2" />
            ) : (
              <KeyRound className="me-2" size={16} aria-hidden="true" />
            )}
            Update Password
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ChangePasswordModal;
